import { useSelector } from "react-redux"
import { RootState } from "src/redux/store"
import KAProgress from "./KAProgress"
import KeywordAssistBreadcrumbs from "./KeywordAssistBreadcrumbs"
import KeywordAssistHeader from "./KeywordAssistHeader"
import { useKeywordAssist } from "../Providers/KAProviders"
import { useGetAgentsStatus } from "../../hooks/useGetAgentsStatus"
import AgentLastRun from "../../Components/AgentLastRun"

/**
 * `KACommon` is a layout/container component that renders the shared layout elements 
 * for the Keyword Assist feature interface.
 * 
 * It structures the top-level section of the Keyword Assist dashboard, including:
 * 1. Navigation Breadcrumbs (`KeywordAssistBreadcrumbs`) - Displays the location trail.
 * 2. Header Actions (`KeywordAssistHeader`) - Displays controls/actions for Keyword Assist.
 * 3. Usage tracking progress visualization (`KAProgress`) - Shows the remaining and used limits for keyword search assists.
 * 
 * ### Sub-components Rendered:
 * - {@link KeywordAssistBreadcrumbs} - Renders the page hierarchy trail.
 * - {@link KeywordAssistHeader} - Handles feature-level settings or actions.
 * - {@link KAProgress} - Manages the state and layout of the usage tracker.
 *   - `KAProgress.Text`: Standard label container.
 *   - `KAProgress.Tooltip`: Displays detailed information about Keyword Assist when hovered.
 *   - `KAProgress.Action`: Triggers actionable workflows like upgrading limits.
 *   - `KAProgress.Bar`: Visual indicator of the consumption percentage.
 * 
 * @component
 * @category Component
 * @subcategory KeywordResearch
 * @example
 * ```tsx
 * import KACommon from './KACommon';
 * 
 * function KeywordAssistPage() {
 *   return (
 *     <div className="keyword-assist-container">
 *       <KACommon />
 *       <main>Main Content</main>
 *     </div>
 *   );
 * }
 * ```
 * 
 * @returns {JSX.Element} The rendered common top layout and progress tracker section.
 */
function KACommon() {
  const { _id: siteId } =
    useSelector((state: RootState) => state?.siteAudit?.siteAuditDetails) ?? {};
  const { data: agentsStatus } = useGetAgentsStatus(siteId);
  const {
    state: { keywordsData }
  } = useKeywordAssist();

  const hasKeywordData = Boolean(
    keywordsData?.page_details && keywordsData.page_details.length > 0 && keywordsData?.last_updated
  );

  const lastRunTimestamp = hasKeywordData ? keywordsData?.last_updated : null;

  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <KeywordAssistBreadcrumbs />
        <div className="flex gap-4 items-center">
          <AgentLastRun timestamp={lastRunTimestamp} showBorder />
          <KeywordAssistHeader />
        </div>
      </div>

      {/* <div aria-label="keyword-assist-progress">
        <KAProgress totalLimit={300} totalUsed={0}>
          <div className="mb-1 flex justify-between">
            <KAProgress.Text>
              <KAProgress.Tooltip
                title={
                  <Typography
                    sx={{
                      ...keywordResearchDashBoardCss?.customTollTipText,
                      lineHeight: 'var(--line-height-22)'
                    }}
                  >
                    The maximum number of Keyword Assist searches you can run each month under your plan, resetting at the start of each billing cycle.
                  </Typography>
                }
              />
            </KAProgress.Text>
            <KAProgress.Action />
          </div>
          <KAProgress.Bar />
        </KAProgress>
      </div> */}
    </>
  )
}

export default KACommon;